import { DataProxy } from '@apollo/client';
import { GET_PLANNED_MILESTONES, GET_PLANNING_BACKLOG_MILESTONE_LIST } from '@bechtel/client-gql';
import {
  CommodityMilestone,
  CrewMemberExpiredMilestoneStats,
  MilestoneStats,
  MilestoneStatus,
  MilestoneStatusCode,
  PersonaType,
  PlanningError,
  PlanningErrorCode,
  PlanningPayload,
  PlanningStatsByCrewMember,
  PlanningWeek,
  PlanningWeekStats,
  UnitOfMeasure,
} from '@bechtel/types';
import produce from 'immer';
import { concat, filter, find, findIndex, forEach, groupBy, nth, some, split } from 'lodash';
import { bankersRound } from '~/utilities/bankers-round';

export enum DND_IDENTIFIER {
  Unplanned = 'UNPLANNED',
  Commodity = 'COMMODITY',
  Milestone = 'MILESTONE',
  Expired = 'EXPIRED',
}

export interface BacklogPlannedMilestones {
  backlogMilestones: any; //TODO: Apollo Cacjhe types
  plannedMilestones: any; //TODO: Apollo Cacjhe types
}

export interface PlanState {
  plannedMilestones: any; //TODO:Apollo cache object types
  sourceStateKey: string;
  destinationStateKey: string;
  commodityMilestones: CommodityMilestone[];
}

export interface UnPlanState {
  backlogPlannedMilestones: BacklogPlannedMilestones; //TODO:Apollo cache object types { backlogMilestones: backlogMilestones,plannedMilestones: plannedMilestones,}
  destinationStateKey: string;
  commodityMilestones: CommodityMilestone[];
  selectedWpList?: string[] | undefined;
}
export const splitIdentifiers = (id: string, seperator: string = '___') => split(id, seperator);

export const getSavePlanningOptimisticResponse = (
  planningWeek: PlanningWeek,
  commodityMilestones: CommodityMilestone[],
  planningWeekStats: PlanningWeekStats[],
  milestoneSource: Map<string | undefined, string | undefined>,
  expiredMilestoneStats: CrewMemberExpiredMilestoneStats
): { savePlanning: PlanningPayload } => {
  //TODO: typename we don't need to change if the server sends..Check and change
  const optimisticPlanningPayload: PlanningPayload = {
    id: '',
    error: [],
    planningStats: [],
    commodityMilestones: [],
    expiredMilestoneCount: expiredMilestoneStats,
  };
  optimisticPlanningPayload['__typename'] = 'PlanningPayload';
  if (!commodityMilestones || commodityMilestones.length == 0) return { savePlanning: optimisticPlanningPayload };

  const destMilesoneStat: MilestoneStats = {
    id: '',
    numMilestones: 0,
    hours: 0,
    quantity: { quantity: 0, unitOfMeasure: UnitOfMeasure.LINEAR_FEET },
  };
  const sourceMilesoneStat: Map<number, MilestoneStats> = new Map();
  let numExpiredCount = 0;

  forEach(commodityMilestones, (cm) => {
    const commodityMilestone: CommodityMilestone = { ...cm };
    const commodityMilestoneStatus: MilestoneStatus = { ...cm.currentStatus };
    if (cm.isExpired) numExpiredCount++;
    commodityMilestone.isExpired = false;
    commodityMilestone.weekNumber = planningWeek.weekNumber;
    commodityMilestone.plannedStartDate = planningWeek.startDate;
    commodityMilestone.plannedEndDate = planningWeek.endDate;
    if (commodityMilestoneStatus.codeI18n !== MilestoneStatusCode.BLOCKED)
      commodityMilestoneStatus.codeI18n = MilestoneStatusCode.PLANNED;

    commodityMilestone.currentStatus = commodityMilestoneStatus;
    optimisticPlanningPayload.commodityMilestones?.push(commodityMilestone);
    //Values to increment in the destination
    destMilesoneStat.hours += commodityMilestone.hours;
    destMilesoneStat.quantity.quantity += commodityMilestone.quantity.quantity;
    destMilesoneStat.numMilestones += 1;
    //Values to decrement in the source
    const sourceStateKey = milestoneSource.get(commodityMilestone.id);
    if (sourceStateKey && sourceStateKey !== DND_IDENTIFIER.Unplanned) {
      const sourcePlanningWeek = parseInt(String(nth(splitIdentifiers(sourceStateKey, '|'), 1)));
      if (sourceMilesoneStat.has(sourcePlanningWeek)) {
        const sourceStat = sourceMilesoneStat.get(sourcePlanningWeek);
        if (sourceStat) {
          //if for the week if some are milestones are dragged, add it to the source
          sourceMilesoneStat.set(sourcePlanningWeek, {
            id: '',
            hours: bankersRound(sourceStat?.hours + commodityMilestone.hours, 0),
            numMilestones: sourceStat?.numMilestones + 1,
            quantity: {
              quantity: sourceStat?.quantity.quantity + commodityMilestone.quantity.quantity,
              unitOfMeasure: UnitOfMeasure.LINEAR_FEET,
            },
          });
        }
      } else {
        //First milestine from the source dragged
        sourceMilesoneStat.set(sourcePlanningWeek, {
          id: '',
          hours: bankersRound(commodityMilestone.hours, 0),
          numMilestones: 1,
          quantity: {
            quantity: commodityMilestone.quantity.quantity,
            unitOfMeasure: UnitOfMeasure.LINEAR_FEET,
          },
        });
      }
    }
  });
  //Form Planning Week stat optimistic response
  const resultPlanningWeekStats = produce(planningWeekStats, (draft) => {
    forEach(draft, (planningWeekStat) => {
      if (planningWeekStat.weekNumber == planningWeek.weekNumber) {
        planningWeekStat.plannedMilestoneStats = {
          ...planningWeekStat.plannedMilestoneStats,
          hours: bankersRound(planningWeekStat.plannedMilestoneStats.hours + destMilesoneStat.hours, 0),
          quantity: {
            quantity: planningWeekStat.plannedMilestoneStats.quantity.quantity + destMilesoneStat.quantity.quantity,
            unitOfMeasure: UnitOfMeasure.LINEAR_FEET,
          },
          numMilestones: planningWeekStat.plannedMilestoneStats.numMilestones + destMilesoneStat.numMilestones,
        };
      }
      if (sourceMilesoneStat.has(planningWeekStat.weekNumber)) {
        const sourceStat = sourceMilesoneStat.get(planningWeekStat.weekNumber);
        if (!sourceStat) return;
        planningWeekStat.plannedMilestoneStats = {
          ...planningWeekStat.plannedMilestoneStats,
          hours: bankersRound(planningWeekStat.plannedMilestoneStats.hours - sourceStat.hours, 0),
          quantity: {
            quantity: planningWeekStat.plannedMilestoneStats.quantity.quantity - sourceStat.quantity.quantity,
            unitOfMeasure: UnitOfMeasure.LINEAR_FEET,
          },
          numMilestones: planningWeekStat.plannedMilestoneStats.numMilestones - sourceStat.numMilestones,
        };
      }
    });
    return draft;
  });
  const groupedPlanningWeekStat = groupBy(resultPlanningWeekStats, (weekStat) => {
    return weekStat.crewMember?.id;
  });
  forEach(groupedPlanningWeekStat, (weekStat) => {
    const planningStat: PlanningStatsByCrewMember = {
      //@ts-ignore weekstat return type is undefined/crew member
      crewMember: { ...weekStat[0].crewMember },
      //@ts-ignore weekstat return type is undefined/crewMemberId
      crewMemberId: weekStat[0].crewMember?.id,
      planningWeekStats: weekStat,
    };
    planningStat.crewMember['__typename'] = 'CrewMember';
    planningStat['__typename'] = 'PlanningStatsByCrewMember';
    optimisticPlanningPayload.planningStats?.push(planningStat);
  });
  if (expiredMilestoneStats) {
    optimisticPlanningPayload.expiredMilestoneCount = {
      id: expiredMilestoneStats.id,
      numExpiredMilestones:
        expiredMilestoneStats.numExpiredMilestones > 0
          ? expiredMilestoneStats.numExpiredMilestones - numExpiredCount
          : expiredMilestoneStats.numExpiredMilestones,
    };
    optimisticPlanningPayload.expiredMilestoneCount['__typename'] = 'CrewMemberExpiredMilestoneStats';
  }
  const response = { savePlanning: optimisticPlanningPayload };
  return response;
};

export const getUnplanOptimisticResponse = (
  commodityMilestones: CommodityMilestone[],
  planningWeekStats: PlanningWeekStats[],
  milestoneSource: Map<string | undefined, string | undefined>,
  expiredMilestoneStats: CrewMemberExpiredMilestoneStats
): { unplan: PlanningPayload } => {
  //TODO: typename we don't need to change if the server sends..Check and change
  const optimisticPlanningPayload: PlanningPayload = {
    id: '',
    error: [],
    planningStats: [],
    commodityMilestones: [],
    expiredMilestoneCount: expiredMilestoneStats,
  };
  optimisticPlanningPayload['__typename'] = 'PlanningPayload';
  if (!commodityMilestones || commodityMilestones.length == 0) return { unplan: optimisticPlanningPayload };

  const sourceMilesoneStat: Map<number, MilestoneStats> = new Map();
  let sourcePlanningWeek = 0;
  let numExpiredCount = 0;

  forEach(commodityMilestones, (cm) => {
    const commodityMilestone: CommodityMilestone = { ...cm };
    const commodityMilestoneStatus: MilestoneStatus = { ...cm.currentStatus };
    commodityMilestone.weekNumber = null;
    commodityMilestone.plannedStartDate = null;
    commodityMilestone.plannedEndDate = null;

    if (commodityMilestoneStatus.codeI18n !== MilestoneStatusCode.BLOCKED)
      commodityMilestoneStatus.codeI18n = MilestoneStatusCode.UNPLANNED;

    commodityMilestone.currentStatus = commodityMilestoneStatus;
    if (cm.isExpired) numExpiredCount++;
    commodityMilestone.isExpired = false; //on unplan, milestone will no longer be expired
    optimisticPlanningPayload.commodityMilestones?.push(commodityMilestone);

    //Values to decrement in the source
    const sourceStateKey = milestoneSource.get(commodityMilestone.id);
    if (sourceStateKey && sourceStateKey !== DND_IDENTIFIER.Unplanned) {
      sourcePlanningWeek = parseInt(String(nth(splitIdentifiers(sourceStateKey, '|'), 1)));
      if (sourceMilesoneStat.has(sourcePlanningWeek)) {
        const sourceStat = sourceMilesoneStat.get(sourcePlanningWeek);
        if (sourceStat) {
          sourceMilesoneStat.set(sourcePlanningWeek, {
            id: '',
            hours: bankersRound(sourceStat?.hours + commodityMilestone.hours, 0),
            numMilestones: sourceStat?.numMilestones + 1,
            quantity: {
              quantity: sourceStat?.quantity.quantity + commodityMilestone.quantity.quantity,
              unitOfMeasure: UnitOfMeasure.LINEAR_FEET,
            },
          });
        }
      } else {
        //First milestone from the source
        sourceMilesoneStat.set(sourcePlanningWeek, {
          id: '',
          hours: bankersRound(commodityMilestone.hours, 0),
          numMilestones: 1,
          quantity: {
            quantity: commodityMilestone.quantity.quantity,
            unitOfMeasure: UnitOfMeasure.LINEAR_FEET,
          },
        });
      }
    }
  });
  //Form Planning Week stat optimistic response
  const resultPlanningWeekStats = produce(planningWeekStats, (draft) => {
    forEach(draft, (planningWeekStat) => {
      if (sourceMilesoneStat.has(planningWeekStat.weekNumber)) {
        const sourceStat = sourceMilesoneStat.get(planningWeekStat.weekNumber);
        if (!sourceStat) return;
        planningWeekStat.plannedMilestoneStats = {
          ...planningWeekStat.plannedMilestoneStats,
          hours: bankersRound(planningWeekStat.plannedMilestoneStats.hours - sourceStat.hours, 0),
          quantity: {
            quantity: planningWeekStat.plannedMilestoneStats.quantity.quantity - sourceStat.quantity.quantity,
            unitOfMeasure: UnitOfMeasure.LINEAR_FEET,
          },
          numMilestones: planningWeekStat.plannedMilestoneStats.numMilestones - sourceStat.numMilestones,
        };
      }
    });
    return draft;
  });
  const groupedPlanningWeekStat = groupBy(resultPlanningWeekStats, (weekStat) => {
    return weekStat.crewMember?.id;
  });
  forEach(groupedPlanningWeekStat, (weekStat) => {
    const planningStat: PlanningStatsByCrewMember = {
      //@ts-ignore crewMember will not be undefined from weekStat[0]
      crewMember: { ...weekStat[0].crewMember },
      //@ts-ignore crewMember will not be undefined from weekStat[0]
      crewMemberId: weekStat[0].crewMember?.id,
      planningWeekStats: weekStat,
    };
    planningStat.crewMember['__typename'] = 'CrewMember';
    planningStat['__typename'] = 'PlanningStatsByCrewMember';
    optimisticPlanningPayload.planningStats?.push(planningStat);
  });

  if (expiredMilestoneStats) {
    optimisticPlanningPayload.expiredMilestoneCount = {
      id: expiredMilestoneStats.id,
      numExpiredMilestones:
        expiredMilestoneStats.numExpiredMilestones > 0
          ? expiredMilestoneStats.numExpiredMilestones - numExpiredCount
          : expiredMilestoneStats.numExpiredMilestones,
    };
    optimisticPlanningPayload.expiredMilestoneCount['__typename'] = 'CrewMemberExpiredMilestoneStats';
  }

  const response = { unplan: optimisticPlanningPayload };
  return response;
};

export const getDestPlannedFromPlannedMilestones = (planState: PlanState) => {
  const { plannedMilestones, sourceStateKey, destinationStateKey, commodityMilestones } = planState;
  return produce(plannedMilestones, (draft) => {
    const sourcePlanningWeekIndex = findIndex(draft, {
      id: sourceStateKey,
    });
    const destinationPlanningWeekIndex = findIndex(draft, {
      id: destinationStateKey,
    });
    if (!draft[destinationPlanningWeekIndex].milestones) draft[destinationPlanningWeekIndex].milestones = [];

    forEach(commodityMilestones, ({ id }) => {
      const sourceMilestone = find(plannedMilestones[sourcePlanningWeekIndex].milestones, {
        id: id,
      });
      const sourceMilestoneIndex = findIndex(draft[sourcePlanningWeekIndex].milestones, {
        id: id,
      });
      draft[sourcePlanningWeekIndex].milestones?.splice(sourceMilestoneIndex, 1);
      const milestoneUpdated = draft[destinationPlanningWeekIndex].milestones?.concat(sourceMilestone);
      draft[destinationPlanningWeekIndex].milestones = milestoneUpdated;
    });
    return draft;
  });
};

export const getDeletedPlannedMilestones = (planState: PlanState) => {
  const { plannedMilestones, sourceStateKey, commodityMilestones } = planState;
  return produce(plannedMilestones, (draft) => {
    const sourcePlanningWeekIndex = findIndex(draft, {
      id: sourceStateKey,
    });
    forEach(commodityMilestones, ({ id }) => {
      const sourceMilestoneIndex = findIndex(draft[sourcePlanningWeekIndex].milestones, {
        id: id,
      });
      draft[sourcePlanningWeekIndex].milestones?.splice(sourceMilestoneIndex, 1);
    });
    return draft;
  });
};

export const getDestPlannedFromUnplannedMilestones = (unPlanState: UnPlanState) => {
  const { backlogPlannedMilestones, destinationStateKey, commodityMilestones } = unPlanState;
  return produce(backlogPlannedMilestones, (draft) => {
    const destinationPlanningWeekIndex = findIndex(draft.plannedMilestones, {
      id: destinationStateKey,
    });
    if (!draft.plannedMilestones[destinationPlanningWeekIndex].milestones)
      draft.plannedMilestones[destinationPlanningWeekIndex].milestones = [];
    forEach(commodityMilestones, ({ id }) => {
      const sourceMilestone = find(draft.backlogMilestones.milestones, {
        id: id,
      });
      const sourceMilestoneIndex = findIndex(draft.backlogMilestones.milestones, {
        id: id,
      });
      draft.backlogMilestones.milestones.splice(sourceMilestoneIndex, 1);
      const milestoneUpdated = draft.plannedMilestones[destinationPlanningWeekIndex].milestones.concat(sourceMilestone);
      draft.plannedMilestones[destinationPlanningWeekIndex].milestones = milestoneUpdated;
    });
    return draft;
  });
};

export const getDestUnPlannedFromPlannedMilestones = (unPlanState: UnPlanState) => {
  const { backlogPlannedMilestones, commodityMilestones, selectedWpList } = unPlanState;
  return produce(backlogPlannedMilestones, (draft) => {
    forEach(commodityMilestones, (milestone) => {
      // Initialize backlogMilestones if it doesn't exist
      if (!draft.backlogMilestones || !draft.backlogMilestones.milestones) {
        draft.backlogMilestones = { milestones: [] };
      }

      const backlogIndex = findIndex(draft?.backlogMilestones?.milestones, { id: milestone.id });
      //if a planned milestone is being unplanned push it to backlog if wp is selected and update planned milestone list
      if (backlogIndex === -1) {
        const wpId =
          milestone.commodity?.materialCommodity?.workPackage?.id || milestone.commodity?.weld?.workPackage?.id;
        const isMilestoneWpSelected =
          selectedWpList && selectedWpList.length && wpId ? some(selectedWpList, (wp) => wpId === wp) : false;
        if (isMilestoneWpSelected) draft.backlogMilestones.milestones.push(milestone);
      }

      const week = find(draft.plannedMilestones, (planningWeek) => {
        return find(planningWeek.milestones, (pm) => pm.id === milestone.id);
      });
      if (week) {
        const weekIndex = findIndex(draft.plannedMilestones, {
          id: week.id,
        });
        const filteredResults = filter(draft.plannedMilestones[weekIndex].milestones, (m) => m.id !== milestone.id);
        draft.plannedMilestones[weekIndex].milestones = filteredResults;
      }
    });
    return draft;
  });
};

export const updateUnplanCache = (
  cache: DataProxy,
  destinationStateKey: string,
  commodityMilestones: CommodityMilestone[],
  selectedWpList: string[],
  foremanId: string,
  numPlanningNextWeeks: number,
  plannedMilestonesSnapshot?: PlanningWeek[],
  backlogMilestonesSnapshot?: CommodityMilestone[],
  planningAiRequestId?: string | null
) => {
  if (destinationStateKey === DND_IDENTIFIER.Unplanned) {
    // Read planned milestones from cache with null safety
    //@ts-ignore Apollo cannot find the connection attribute in typescript
    const cacheData = cache.readQuery({
      query: GET_PLANNED_MILESTONES,
      variables: {
        input: {
          persona: PersonaType.FOREMAN,
          crewMemberId: foremanId,
          numNextWeeks: numPlanningNextWeeks,
          ...(planningAiRequestId ? { planningAiRequestId } : {}),
        },
      },
    });

    //@ts-ignore
    const plannedMilestones = cacheData?.plannedMilestones?.length
      ? cacheData.plannedMilestones
      : plannedMilestonesSnapshot;

    const backlogPlannedMilestones = {
      backlogMilestones: { milestones: [] },
      plannedMilestones: plannedMilestones,
    };

    if (selectedWpList && selectedWpList.length > 0) {
      //@ts-ignore Apollo cannot find the connection attribute in typescript
      const backlogCacheData = cache.readQuery({
        query: GET_PLANNING_BACKLOG_MILESTONE_LIST,
        variables: {
          input: {
            workPackageIds: selectedWpList,
            foremanId: foremanId,
            ...(planningAiRequestId ? { planningAiRequestId } : {}),
          },
        },
      });

      //@ts-ignore
      const backlogData = backlogCacheData?.backlogMilestones;
      // Ensure backlogMilestones has the proper structure with milestones array
      // Ensure backlogMilestones has the proper structure with milestones array
      backlogPlannedMilestones.backlogMilestones = backlogData
        ? { ...backlogData, milestones: backlogData?.milestones || [] }
        : backlogMilestonesSnapshot
          ? { milestones: backlogMilestonesSnapshot }
          : { milestones: [] };
    }
    const updated = getDestUnPlannedFromPlannedMilestones({
      backlogPlannedMilestones,
      destinationStateKey,
      commodityMilestones,
      selectedWpList,
    });
    if (selectedWpList && selectedWpList.length > 0) {
      cache.writeQuery({
        query: GET_PLANNING_BACKLOG_MILESTONE_LIST,
        variables: {
          input: {
            workPackageIds: selectedWpList,
            foremanId: foremanId,
            ...(planningAiRequestId ? { planningAiRequestId } : {}),
          },
        },
        data: {
          backlogMilestones: updated.backlogMilestones,
        },
      });
    }
    cache.writeQuery({
      query: GET_PLANNED_MILESTONES,
      variables: {
        input: {
          persona: PersonaType.FOREMAN,
          crewMemberId: foremanId,
          numNextWeeks: numPlanningNextWeeks,
          ...(planningAiRequestId ? { planningAiRequestId } : {}),
        },
      },
      data: {
        plannedMilestones: updated.plannedMilestones,
      },
    });
  }
};

export const updateCache = (
  cache: DataProxy,
  sourceStateKey: string,
  destinationStateKey: string,
  commodityMilestones: CommodityMilestone[],
  foremanId: string,
  selectedWpList: string[],
  numPlanningNextWeeks: number,
  planningAiRequestId?: string | null
) => {
  // Fetch the existing planned milestones from the cache
  // NOTE: Here the plannedMilestones is the @connection name of the query
  //@ts-ignore Apollo cannot find the connection attribute in typescript
  const result =
    cache.readQuery({
      query: GET_PLANNED_MILESTONES as any,
      variables: {
        input: {
          persona: PersonaType.FOREMAN,
          crewMemberId: foremanId,
          numNextWeeks: numPlanningNextWeeks,
          ...(planningAiRequestId ? { planningAiRequestId } : {}),
        },
      } as any,
    }) || {};
  const plannedMilestones = result.plannedMilestones || [];

  //Commodity/Milestones moved between planning weeks
  if (sourceStateKey !== DND_IDENTIFIER.Unplanned) {
    const destPlannedWeek = getDestPlannedFromPlannedMilestones({
      plannedMilestones: plannedMilestones,
      sourceStateKey: sourceStateKey,
      destinationStateKey: destinationStateKey,
      commodityMilestones: commodityMilestones,
    });
    cache.writeQuery({
      query: GET_PLANNED_MILESTONES as any,
      variables: {
        input: {
          persona: PersonaType.FOREMAN,
          crewMemberId: foremanId,
          numNextWeeks: numPlanningNextWeeks,
          ...(planningAiRequestId ? { planningAiRequestId } : {}),
        },
      },
      data: { plannedMilestones: destPlannedWeek },
    });
  } else {
    // Fetch the existing backlog milestones from the cache
    // NOTE: Here the backlogMilestones is the @connection name of the query
    //Commodity/Milestones moved from backlog to planning weeks
    //@ts-ignore Apollo cannot find the connection attribute in typescript
    const { backlogMilestones } = cache.readQuery({
      query: GET_PLANNING_BACKLOG_MILESTONE_LIST,
      variables: {
        input: {
          workPackageIds: selectedWpList,
          foremanId: foremanId,
        },
      },
    });

    const backlogPlannedMilestones = {
      backlogMilestones: backlogMilestones,
      plannedMilestones: plannedMilestones,
    };

    const destPlannedWeek = getDestPlannedFromUnplannedMilestones({
      backlogPlannedMilestones: backlogPlannedMilestones,
      destinationStateKey: destinationStateKey,
      commodityMilestones: commodityMilestones,
    });

    cache.writeQuery({
      query: GET_PLANNING_BACKLOG_MILESTONE_LIST,
      variables: {
        input: {
          workPackageIds: selectedWpList,
          foremanId: foremanId,
        },
      },
      data: {
        backlogMilestones: destPlannedWeek.backlogMilestones,
      },
    });
    cache.writeQuery({
      query: GET_PLANNED_MILESTONES,
      variables: {
        input: {
          persona: PersonaType.FOREMAN,
          crewMemberId: foremanId,
          numNextWeeks: numPlanningNextWeeks,
          ...(planningAiRequestId ? { planningAiRequestId } : {}),
        },
      },
      data: {
        plannedMilestones: destPlannedWeek.plannedMilestones,
      },
    });
  }
};

export const invalidateCache = (
  cache: DataProxy,
  sourceStateKey: string,
  destinationStateKey: string,
  commodityMilestones: CommodityMilestone[],
  foremanId: string,
  selectedWpList: string[],
  numPlanningNextWeeks: number
) => {
  // Fetch the existing planned milestones from the cache
  // NOTE: Here the plannedMilestones is the @connection name of the query
  //@ts-ignore Apollo cannot find the connection attribute in typescript
  const { plannedMilestones } = cache.readQuery({
    query: GET_PLANNED_MILESTONES,
    variables: {
      input: {
        persona: PersonaType.FOREMAN,
        crewMemberId: foremanId,
        numNextWeeks: numPlanningNextWeeks,
      },
    },
  });
  //Commodity/Milestones moved between planning weeks
  if (sourceStateKey !== DND_IDENTIFIER.Unplanned) {
    const updatedPlannedWeek = getDeletedPlannedMilestones({
      plannedMilestones: plannedMilestones,
      sourceStateKey: sourceStateKey,
      destinationStateKey: destinationStateKey,
      commodityMilestones: commodityMilestones,
    });
    cache.writeQuery({
      query: GET_PLANNED_MILESTONES,
      data: { plannedMilestones: updatedPlannedWeek },
    });
  } else {
    // Fetch the existing backlog milestones from the cache
    // NOTE: Here the backlogMilestones is the @connection name of the query
    //Commodity/Milestones moved from backlog to planning weeks
    //@ts-ignore Apollo cannot find the connection attribute in typescript
    const { backlogMilestones } = cache.readQuery({
      query: GET_PLANNING_BACKLOG_MILESTONE_LIST,
      variables: {
        input: {
          workPackageIds: selectedWpList,
          foremanId: foremanId,
        },
      },
    });

    const backlogPlannedMilestones = {
      backlogMilestones: backlogMilestones,
      plannedMilestones: plannedMilestones,
    };

    const destPlannedWeek = getDestPlannedFromUnplannedMilestones({
      backlogPlannedMilestones: backlogPlannedMilestones,
      destinationStateKey: destinationStateKey,
      commodityMilestones: commodityMilestones,
    });

    cache.writeQuery({
      query: GET_PLANNING_BACKLOG_MILESTONE_LIST,
      variables: {
        input: {
          workPackageIds: selectedWpList,
          foremanId: foremanId,
        },
      },
      data: {
        backlogMilestones: destPlannedWeek.backlogMilestones,
      },
    });
    cache.writeQuery({
      query: GET_PLANNED_MILESTONES,
      variables: {
        input: {
          persona: PersonaType.FOREMAN,
          crewMemberId: foremanId,
          numNextWeeks: numPlanningNextWeeks,
        },
      },
      data: {
        plannedMilestones: destPlannedWeek.plannedMilestones,
      },
    });
  }
};

export const deleteApproveClaimedMilestone = (
  planningError: PlanningError[],
  milestoneSource: Map<string | undefined, string | undefined>,
  cache: DataProxy,
  destinationStateKey: string,
  foremanId: string,
  selectedWpList: string[],
  numPlanningNextWeeks: number
) => {
  const approvedClaimedError = filter(
    planningError,
    (planningError) =>
      planningError.errorCode == PlanningErrorCode.MILESTONE_IS_APPROVED ||
      planningError.errorCode == PlanningErrorCode.MILESTONE_IS_CLAIMED
  );
  if (approvedClaimedError && approvedClaimedError.length > 0) {
    let approvedClaimedMilestones: CommodityMilestone[] = [];
    forEach(approvedClaimedError, (planError) => {
      if (planError.commodityMilestones)
        approvedClaimedMilestones = concat(approvedClaimedMilestones, planError.commodityMilestones);
    });
    // Group milestones by source to batch invalidateCache calls
    const milestonesBySource = new Map<string, CommodityMilestone[]>();
    forEach(approvedClaimedMilestones, (commodityMilestone) => {
      const sourceStateKey = milestoneSource.get(commodityMilestone.id);
      if (!sourceStateKey) return;
      if (!milestonesBySource.has(sourceStateKey)) {
        milestonesBySource.set(sourceStateKey, []);
      }
      milestonesBySource.get(sourceStateKey)!.push(commodityMilestone);
    });
    // Call invalidateCache once per source group
    milestonesBySource.forEach((milestones, sourceStateKey) => {
      invalidateCache(
        cache,
        sourceStateKey,
        destinationStateKey,
        milestones,
        foremanId,
        selectedWpList,
        numPlanningNextWeeks
      );
    });
  }
};
